/**
 * Locations Panel Component
 *
 * Scrollable list of pickup locations with search/filter.
 * On mobile: fills its container as a full-height panel.
 * On desktop: renders as a fixed-width sidebar.
 *
 * Subscribes to locationStore for reactive location data.
 * This is NOT the shopping cart - it's for browsing locations.
 *
 * @element peach-locations-drawer
 *
 * @fires location-selected - Emitted when a location is clicked
 *
 * @example
 * ```html
 * <peach-locations-drawer></peach-locations-drawer>
 * ```
 */

import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { locationStore } from "@/features/locations/stores/location-store";
import { mapViewStore } from "@/features/map/stores/map-view-store";
import { uiStore, type ActiveTab } from "@/stores/ui-store";
import type { Location } from "@/types/location";
import { cn } from "@/utils/cn";
import { GeolocationService } from "@/services/geolocation-service";
import "./locations-list";
import "./location-details";
import "./map-filters";
import "@/features/ui/icon";
import logoUrl from "/logo-footer-red.svg?url";

@customElement("peach-locations-drawer")
export class LocationsDrawerComponent extends LitElement {
  /**
   * Render in light DOM to enable Tailwind CSS classes
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Locations from store
   */
  @state()
  declare private locations: Location[];

  /**
   * Loading state
   */
  @state()
  declare private isLoading: boolean;

  /**
   * Error message
   */
  @state()
  declare private error: string | null;

  /**
   * Selected location ID (from location store, drives desktop details panel)
   */
  @state()
  declare private selectedLocationId: number | null;

  /**
   * User's current location (if geolocation is enabled)
   */
  @state()
  declare private userLocation: { lat: number; lng: number } | null;

  /**
   * Geolocation loading state
   */
  @state()
  declare private isGettingLocation: boolean;

  /**
   * Active tab (for desktop view toggle)
   */
  @state()
  declare private activeTab: ActiveTab;

  /**
   * Get the selected location from the locations array
   */
  private get selectedLocation(): Location | null {
    if (!this.selectedLocationId) return null;
    return this.locations.find((loc) => loc.id === this.selectedLocationId) ?? null;
  }

  /**
   * Store unsubscribe functions
   */
  private unsubscribeLocation?: () => void;
  private unsubscribeUi?: () => void;

  constructor() {
    super();
    this.locations = [];
    this.isLoading = false;
    this.error = null;
    this.selectedLocationId = null;
    this.userLocation = null;
    this.isGettingLocation = false;
    this.activeTab = "browse";
  }

  /**
   * Store subscription selector
   */
  private static readonly storeSelector = (state: any) => ({
    tours: state.tours,
    markets: state.markets,
    isLoading: state.isLoading,
    error: state.error,
    selectedLocationId: state.selectedLocationId,
  });

  /**
   * Store subscription equality function
   *
   * @remarks
   * Only triggers re-render when relevant properties change.
   */
  private static readonly storeEqualityFn = (a: any, b: any) =>
    a.tours.length === b.tours.length &&
    a.markets.length === b.markets.length &&
    a.isLoading === b.isLoading &&
    a.error === b.error &&
    a.selectedLocationId === b.selectedLocationId;

  /**
   * Subscribe to location store on connect
   *
   * @remarks
   * fireImmediately: true eliminates need for manual initialization.
   */
  connectedCallback() {
    super.connectedCallback();

    this.unsubscribeLocation = locationStore.subscribe(
      LocationsDrawerComponent.storeSelector,
      (selected) => {
        this.locations = [...selected.tours, ...selected.markets];
        this.isLoading = selected.isLoading;
        this.error = selected.error;
        this.selectedLocationId = selected.selectedLocationId;
      },
      {
        equalityFn: LocationsDrawerComponent.storeEqualityFn,
        fireImmediately: true,
      }
    );

    this.unsubscribeUi = uiStore.subscribe(
      (state) => state.activeTab,
      (activeTab) => {
        this.activeTab = activeTab;
      },
      { fireImmediately: true }
    );
  }

  /**
   * Unsubscribe from stores on disconnect
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribeLocation?.();
    this.unsubscribeUi?.();
  }

  /**
   * Handle desktop view toggle
   */
  private handleViewToggle(tab: ActiveTab) {
    uiStore.getState().setActiveTab(tab);
  }

  /**
   * Bubble location selection up to parent
   */
  private handleLocationSelected(e: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent("location-selected", {
        detail: e.detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Get user's current location and center map on it
   *
   * @remarks
   * Uses GeolocationService for browser location access.
   * Updates map center, search point, and search query via store.
   */
  private async handleUseCurrentLocation() {
    if (!GeolocationService.isSupported()) {
      console.error("Geolocation is not supported by this browser");
      return;
    }

    this.isGettingLocation = true;

    try {
      const location = await GeolocationService.getCurrentLocation();
      this.userLocation = location;

      mapViewStore.getState().setMapCenter({
        lat: location.lat,
        lng: location.lng,
        zoom: 10,
      });

      locationStore.getState().setSearchPoint({
        lat: location.lat,
        lng: location.lng,
        radiusMiles: 50,
      });

      locationStore.getState().updateFilters({ searchQuery: "Current Location" });
    } catch (error) {
      console.error("Error getting current location:", error);
    } finally {
      this.isGettingLocation = false;
    }
  }

  /**
   * Render the header with logo and search
   *
   * @remarks
   * Hidden on mobile - local-pickup-view renders a shared header for all tabs.
   * Only visible on desktop as part of the sidebar.
   */
  private renderHeader() {
    return html`
      <header class="hidden md:flex flex-col items-center px-4 pt-3 pb-4 bg-cream flex-shrink-0 border-b border-default">
        <div class="flex items-center justify-between w-full mb-4">
          <a href="/" class="focus:outline-none focus:ring-2 focus:ring-primary rounded">
            <img
              src="${logoUrl}"
              alt="The Peach Truck"
              class="h-[40px] w-auto"
            />
          </a>

          <!-- Desktop view toggle -->
          <div class="flex bg-surface rounded-lg p-1 shadow-sm border border-default">
            <button
              @click=${() => this.handleViewToggle("browse")}
              class=${cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                this.activeTab === "browse"
                  ? "bg-cream text-brand shadow-sm"
                  : "text-muted hover:text-default"
              )}
              aria-label="Map view"
              aria-pressed=${this.activeTab === "browse"}
            >
              <peach-icon name="map" size="sm"></peach-icon>
              Map
            </button>
            <button
              @click=${() => this.handleViewToggle("schedule")}
              class=${cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                this.activeTab === "schedule"
                  ? "bg-cream text-brand shadow-sm"
                  : "text-muted hover:text-default"
              )}
              aria-label="Schedule view"
              aria-pressed=${this.activeTab === "schedule"}
            >
              <peach-icon name="calendar" size="sm"></peach-icon>
              Schedule
            </button>
          </div>
        </div>

        <div class="w-full">
          <peach-map-filters
            ?is-getting-location=${this.isGettingLocation}
            @use-current-location=${this.handleUseCurrentLocation}
          ></peach-map-filters>
        </div>
      </header>
    `;
  }

  render() {
    const hasSelection = this.selectedLocationId !== null;

    return html`
      <!-- Flex container that grows when details panel is visible -->
      <div class=${cn(
        "relative h-full",
        "md:flex md:flex-row md:transition-all md:duration-300",
      )}>
        <!-- Main locations sidebar / mobile full panel -->
        <aside
          class=${cn(
            "bg-cream shadow-2xl z-20",
            // Mobile: fills the full panel area (parent controls visibility)
            "flex flex-col w-full h-full overflow-hidden",
            // Desktop: fixed-width sidebar, no shadow
            "md:w-96 md:relative md:h-full md:shadow-none md:flex-shrink-0"
          )}
          role="complementary"
          aria-label="Pickup locations"
        >
          ${this.renderHeader()}

          <!-- Locations list - always visible (no peek/collapse behavior) -->
          <div class="flex-1 overflow-y-auto min-h-0">
            <peach-locations-list
              @location-selected=${this.handleLocationSelected}
            ></peach-locations-list>
          </div>
        </aside>

        <!-- Desktop details panel (now in flex flow, not absolute) -->
        <aside
          class=${cn(
            "bg-cream overflow-hidden border-l border-default",
            // Hide entirely on mobile - bottom sheet handles this
            "hidden md:flex md:flex-col md:flex-shrink-0",
            // Width transition for smooth open/close
            "md:transition-[width,opacity] md:duration-300 md:ease-in-out",
            hasSelection ? "md:w-96 md:opacity-100" : "md:w-0 md:opacity-0"
          )}
          aria-hidden=${!hasSelection}
          role="complementary"
          aria-label="Location details"
        >
          ${hasSelection
            ? html`
                <peach-location-details
                  class="flex-1 min-h-0 overflow-hidden flex flex-col w-96"
                  .location=${this.selectedLocation}
                ></peach-location-details>
              `
            : ""}
        </aside>
      </div>
    `;
  }
}

/**
 * TypeScript declaration for JSX/Liquid integration
 */
declare global {
  interface HTMLElementTagNameMap {
    "peach-locations-drawer": LocationsDrawerComponent;
  }
}
